import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import SessionService from "../../../../services/session_service";
import MediaService from "../../../../services/media_service";
import moment from "moment";
import { toast } from "react-toastify";

const IDLE_TIMEOUT_MS = 60 * 1000;  // 1 minuto sem interação → entra em idle
const AULAS_DISPLAY_MS = 30 * 1000; // 30 segundos mostrando a tela de aulas entre mídias

/**
 * Calcula quanto tempo (ms) uma mídia deve ficar visível.
 * - single: usa slideDuration (padrão 8s)
 * - carousel: slideDuration × quantidade de imagens
 */
const getMediaDuration = (media) => {
    const baseDuration = (media.slideDuration || 8) * 1000;
    if (media.type === "carousel" && media.carouselImages?.length > 0) {
        return baseDuration * media.carouselImages.length;
    }
    return baseDuration;
};

export const useTVDisplay = () => {
    const { idTenant, tenantLogo } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [medias, setMedias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(moment());
    const [isIdle, setIsIdle] = useState(false);
    const [kioskBackground, setKioskBackground] = useState(null);

    // ── Rotação Idle: alterna entre "media" e "aulas" ──
    const [idleView, setIdleView] = useState("media"); // "media" | "aulas"
    const [mediaIndex, setMediaIndex] = useState(0);
    const rotationTimerRef = useRef(null);

    // ── 1. Fetch sessions for today ──
    useEffect(() => {
        let isMounted = true;

        const fetchTodaySessions = async () => {
            if (!idTenant) return;
            
            setLoading(true);
            try {
                const todayStr = moment().format("YYYY-MM-DD");
                const dailySessions = await SessionService.getSessions(idTenant, todayStr, todayStr);
                
                if (isMounted) {
                    setSessions(dailySessions || []);
                }

                // Buscar fundo do totem
                import("firebase/firestore").then(({ doc, getDoc }) => {
                    import("../../../../services/firebase").then(({ db }) => {
                        getDoc(doc(db, "tenants", idTenant)).then(snap => {
                            if (isMounted && snap.exists() && snap.data().kioskBackground) {
                                setKioskBackground(snap.data().kioskBackground);
                            }
                        });
                    });
                });
            } catch (error) {
                console.error("Erro ao buscar sessões para a TV:", error);
                if (isMounted) toast.error("Não foi possível carregar a agenda do dia.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchTodaySessions();

        // Refetch fully every hour
        const hourlyInterval = setInterval(fetchTodaySessions, 60 * 60 * 1000);
        
        return () => {
            isMounted = false;
            clearInterval(hourlyInterval);
        };
    }, [idTenant]);

    // ── 2. Fetch active medias ──
    useEffect(() => {
        let isMounted = true;

        const fetchMedias = async () => {
            if (!idTenant) return;
            try {
                const todayStr = moment().format("YYYY-MM-DD");
                const activeMedias = await MediaService.getActiveForToday(idTenant, todayStr);
                if (isMounted) {
                    setMedias(activeMedias || []);
                }
            } catch (error) {
                console.error("Erro ao buscar mídias:", error);
            }
        };

        fetchMedias();

        // Refetch medias every 30 minutes
        const interval = setInterval(fetchMedias, 30 * 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [idTenant]);

    // ── 3. Keep current time ticking every minute ──
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(moment());
        }, 1000 * 60);
        
        return () => clearInterval(timer);
    }, []);

    // ── 4. Idle detection (1 minute) ──
    const resetIdle = useCallback(() => {
        setIsIdle(false);
        setIdleView("media");
        setMediaIndex(0);
    }, []);

    useEffect(() => {
        let idleTimer;

        const startIdleTimer = () => {
            clearTimeout(idleTimer);
            setIsIdle(false);
            setIdleView("media");
            setMediaIndex(0);
            idleTimer = setTimeout(() => {
                setIsIdle(true);
            }, IDLE_TIMEOUT_MS);
        };

        // Eventos que resetam a inatividade
        const events = ["mousemove", "mousedown", "keypress", "touchstart", "scroll"];
        events.forEach(event => window.addEventListener(event, startIdleTimer));

        startIdleTimer();

        return () => {
            events.forEach(event => window.removeEventListener(event, startIdleTimer));
            clearTimeout(idleTimer);
        };
    }, []);

    // ── 4.1 Rotação Idle: Mídia → Aulas → Próxima Mídia → Aulas → ... ──
    //
    // Fluxo:
    //   [Mídia 1 (8s ou carousel completo)] → [Aulas 30s] → [Mídia 2] → [Aulas 30s] → ...
    //
    // Se só tem 1 mídia, alterna: [Mídia] → [Aulas] → [Mídia] → ...
    // Se não tem mídia, fica em "aulas" permanente.
    useEffect(() => {
        if (!isIdle || medias.length === 0) {
            clearTimeout(rotationTimerRef.current);
            return;
        }

        const currentMedia = medias[mediaIndex] || medias[0];

        if (idleView === "media") {
            // Mostrar mídia pelo tempo total dela, depois trocar para "aulas"
            const mediaDuration = getMediaDuration(currentMedia);
            rotationTimerRef.current = setTimeout(() => {
                setIdleView("aulas");
            }, mediaDuration);
        } else {
            // Tela de "aulas" por 30s, depois ir para a próxima mídia
            rotationTimerRef.current = setTimeout(() => {
                setMediaIndex(prev => (prev + 1) % medias.length);
                setIdleView("media");
            }, AULAS_DISPLAY_MS);
        }

        return () => clearTimeout(rotationTimerRef.current);
    }, [isIdle, idleView, mediaIndex, medias]);

    // ── 5. Derived State: classes to show ──
    const currentSessions = useMemo(() => {
        const nowStr = currentTime.format("HH:mm");

        return sessions.filter(session => {
            if (!session.startTime || !session.endTime) return false;
            
            const startMoment = moment(session.startTime, "HH:mm");
            const endMoment = moment(session.endTime, "HH:mm");
            const currentMoment = moment(nowStr, "HH:mm");

            return currentMoment.isBetween(startMoment, endMoment, null, '[]');
        }).sort((a, b) => a.startTime.localeCompare(b.startTime));

    }, [sessions, currentTime]);

    // A mídia atual para o overlay (apenas 1 por vez, o TVMediaCarousel cuida dos slides internos)
    const currentMedia = medias[mediaIndex] || null;

    // Mostrar overlay quando idle + view é "media" + há mídia disponível
    const showMediaOverlay = isIdle && medias.length > 0 && idleView === "media";

    return {
        currentTime,
        currentSessions,
        loading,
        tenantLogo,
        medias,
        currentMedia,
        isIdle,
        idleView,
        showMediaOverlay,
        resetIdle,
        kioskBackground
    };
};
